/**
 * Fixed File Upload Service for Emanta Platform
 *
 * This is a drop-in replacement for the current upload-service.js
 * that fixes the Firebase Storage upload issues while maintaining
 * the same API interface.
 */

import storageService from "./admin/storage-service";
import { useToast } from "vue-toastification";

export const ALLOWED_UPLOAD_FOLDERS = ["logos", "services", "conversations", "pdfs"];

/**
 * Upload states for tracking progress
 */
export const UploadState = {
  PENDING: "pending",
  CREATING_RECORD: "creating_record",
  UPLOADING: "uploading",
  COMPLETING: "completing",
  COMPLETED: "completed",
  ERROR: "error",
  CANCELLED: "cancelled",
};

/**
 * File upload progress information
 */
export class UploadProgress {
  constructor() {
    this.state = UploadState.PENDING;
    this.progress = 0;
    this.message = "";
    this.error = null;
    this.storageId = null;
    this.downloadUrl = null;
  }

  updateState(state, message = "", progress = null) {
    this.state = state;
    this.message = message;
    if (progress !== null) {
      this.progress = progress;
    }
  }

  setError(error) {
    this.state = UploadState.ERROR;
    this.error = error instanceof Error ? error : new Error(String(error));
    this.message = this.error.message;
  }
}

/**
 * Main function to upload a file using XMLHttpRequest for proper progress tracking
 * @param {File} file - The file to upload
 * @param {Object} options - Upload configuration
 * @param {string} options.folder - Storage folder ('logos', 'services', or 'conversations')
 * @param {Object} [options.metadata] - Optional backend file metadata
 * @param {Function} [options.onProgress] - Progress callback
 * @param {Function} [options.onStateChange] - State change callback
 * @returns {Promise<Object>} Upload result with storageId and downloadUrl
 */
export async function createAndUploadFile(file, options = {}) {
  const {
    folder,
    metadata,
    orgContext = 'tenant',
    onProgress = () => {},
    onStateChange = () => {},
  } = options;

  if (!folder || !ALLOWED_UPLOAD_FOLDERS.includes(folder)) {
    throw new Error(
      `Invalid folder specified. Must be one of: ${ALLOWED_UPLOAD_FOLDERS.join(", ")}`,
    );
  }

  const progress = new UploadProgress();

  const updateProgress = (state, message, progressValue) => {
    progress.updateState(state, message, progressValue);
    onProgress(progress);
    onStateChange(progress.state);
  };

  try {
    // Step 1: Validate file
    updateProgress(UploadState.PENDING, "Validating file...", 0);
    const validation = await validateFile(file);
    if (!validation.isValid) {
      throw new Error(
        `File validation failed: ${validation.errors.join(", ")}`,
      );
    }

    // Step 2: Get presigned URL from backend
    updateProgress(UploadState.CREATING_RECORD, "Getting upload URL...", 10);

    const storageResponse = await storageService.createFileRecord({
      originalName: file.name,
      mimeType: file.type || "application/octet-stream",
      folder: folder,
      metadata,
    }, orgContext);

    const { id: storageId, uploadUrl } = storageResponse.data;

    if (!uploadUrl) {
      throw new Error("No upload URL received from backend");
    }

    progress.storageId = storageId;
    updateProgress(UploadState.CREATING_RECORD, "Upload URL received", 20);

    // Step 3: Upload directly to presigned URL using XMLHttpRequest
    updateProgress(UploadState.UPLOADING, "Uploading file...", 30);

    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round(
            (event.loaded / event.total) * 100,
          );
          // Scale progress from 30% to 90%
          const scaledProgress = 30 + percentComplete * 0.6;
          updateProgress(
            UploadState.UPLOADING,
            "Uploading file...",
            scaledProgress,
          );
        }
      });

      // Handle completion
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          // Try to parse error response
          let errorMessage = `Upload failed with status: ${xhr.status}`;

          // Check for CORS error indicators
          if (xhr.status === 0) {
            errorMessage =
              "CORS error: The request was blocked by CORS policy. Check your Firebase Storage CORS configuration.";
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              if (errorData.error && errorData.error.message) {
                errorMessage = errorData.error.message;
              }
            } catch (e) {
              // Not JSON, use status text
              if (xhr.statusText) {
                errorMessage = `Upload failed: ${xhr.statusText}`;
              }
            }
          }
          reject(new Error(errorMessage));
        }
      });

      // Handle errors
      xhr.addEventListener("error", () => {
        reject(
          new Error(
            "Network error during upload. Please check your connection and try again.",
          ),
        );
      });

      xhr.addEventListener("abort", () => {
        reject(new Error("Upload cancelled"));
      });

      // Configure request
      xhr.open("PUT", uploadUrl);

      // Set proper headers for Firebase Storage
      // The Content-Type must match what was used when generating the presigned URL
      const contentType = file.type || "application/octet-stream";
      xhr.setRequestHeader("Content-Type", contentType);

      // Send the file
      xhr.send(file);
    });

    updateProgress(UploadState.UPLOADING, "Upload completed", 90);

    // Generate download URL by removing query parameters
    const downloadUrl = uploadUrl.split("?")[0];
    progress.downloadUrl = downloadUrl;

    updateProgress(UploadState.COMPLETED, "Upload completed successfully", 100);

    return {
      success: true,
      storageId,
      downloadUrl,
      originalName: file.name,
      size: file.size,
      mimeType: file.type,
    };
  } catch (error) {
    progress.setError(error);
    onProgress(progress);
    onStateChange(progress.state);

    console.error("Upload error:", error);
    throw error;
  }
}

/**
 * Upload multiple files sequentially
 * @param {File[]} files - Array of files to upload
 * @param {Object} options - Upload options
 * @param {string} options.folder - Storage folder
 * @param {number} [options.maxConcurrent=2] - Max concurrent uploads
 * @param {Function} [options.onProgress] - Overall progress callback
 * @param {Function} [options.onFileComplete] - Individual file completion callback
 * @param {Function} [options.onFileError] - Individual file error callback
 * @returns {Promise<Object>} Upload results summary
 */
export async function createAndUploadMultipleFiles(files, options = {}) {
  const {
    folder,
    maxConcurrent = 2,
    onProgress = () => {},
    onFileComplete = () => {},
    onFileError = () => {},
  } = options;

  if (!Array.isArray(files) || files.length === 0) {
    throw new Error("Invalid files array provided");
  }

  const results = {
    successful: [],
    failed: [],
    totalFiles: files.length,
    completedCount: 0,
    failedCount: 0,
  };

  const updateOverallProgress = () => {
    const overallProgress =
      ((results.completedCount + results.failedCount) / results.totalFiles) *
      100;
    onProgress(overallProgress, results);
  };

  // Process files in batches to avoid overwhelming the system
  for (let i = 0; i < files.length; i += maxConcurrent) {
    const batch = files.slice(i, i + maxConcurrent);

    const batchPromises = batch.map(async (file, batchIndex) => {
      const globalIndex = i + batchIndex;

      try {
        const result = await createAndUploadFile(file, {
          folder,
          onProgress: () => {
            // Individual file progress can be reported here if needed
          },
          onStateChange: () => {
            // Individual file state changes
          },
        });

        result.originalIndex = globalIndex;
        results.successful.push(result);
        results.completedCount++;
        onFileComplete(file, result, globalIndex);
        updateOverallProgress();

        return { success: true, file, result, index: globalIndex };
      } catch (error) {
        const errorResult = {
          file,
          error,
          index: globalIndex,
          originalName: file.name,
        };

        results.failed.push(errorResult);
        results.failedCount++;
        onFileError(file, error, globalIndex);
        updateOverallProgress();

        return { success: false, file, error, index: globalIndex };
      }
    });

    // Wait for current batch to complete before starting next batch
    await Promise.allSettled(batchPromises);
  }

  return results;
}

/**
 * Validate file before upload
 * @param {File} file - File to validate
 * @param {Object} constraints - Validation constraints
 * @returns {Promise<Object>} Validation result
 */
export function validateFile(file, constraints = {}) {
  const {
    maxSize = 10 * 1024 * 1024, // 10MB default
    allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "application/pdf",
    ],
    minWidth = 0,
    minHeight = 0,
    maxWidth = Infinity,
    maxHeight = Infinity,
  } = constraints;

  return new Promise((resolve) => {
    const errors = [];

    if (!file || !(file instanceof File)) {
      errors.push("Invalid file");
      return resolve({ isValid: false, errors });
    }

    if (file.size > maxSize) {
      errors.push(`File size exceeds ${formatFileSize(maxSize)} limit`);
    }

    if (file.size === 0) {
      errors.push("File is empty");
    }

    // Allow files with empty mime type (some browsers don't set it)
    if (file.type && !allowedTypes.includes(file.type)) {
      errors.push(
        `File type not supported. Allowed: ${allowedTypes.join(", ")}`,
      );
    }

    // For images, validate dimensions if specified
    if (
      file.type &&
      file.type.startsWith("image/") &&
      (minWidth > 0 ||
        minHeight > 0 ||
        maxWidth < Infinity ||
        maxHeight < Infinity)
    ) {
      const img = new Image();
      img.onload = () => {
        if (img.width < minWidth) {
          errors.push(
            `Image width must be at least ${minWidth}px (current: ${img.width}px)`,
          );
        }
        if (img.height < minHeight) {
          errors.push(
            `Image height must be at least ${minHeight}px (current: ${img.height}px)`,
          );
        }
        if (img.width > maxWidth) {
          errors.push(
            `Image width must not exceed ${maxWidth}px (current: ${img.width}px)`,
          );
        }
        if (img.height > maxHeight) {
          errors.push(
            `Image height must not exceed ${maxHeight}px (current: ${img.height}px)`,
          );
        }
        URL.revokeObjectURL(img.src);
        resolve({
          isValid: errors.length === 0,
          errors,
          dimensions: { width: img.width, height: img.height },
        });
      };
      img.onerror = () => {
        errors.push("Invalid or corrupted image file");
        URL.revokeObjectURL(img.src);
        resolve({ isValid: false, errors });
      };
      img.src = URL.createObjectURL(file);
    } else {
      resolve({ isValid: errors.length === 0, errors });
    }
  });
}

/**
 * Delete uploaded file
 * @param {number} storageId - Storage ID of file to delete
 * @returns {Promise<boolean>} Success status
 */
export async function deleteUploadedFile(storageId) {
  try {
    await storageService.deleteFile(storageId);
    return true;
  } catch (error) {
    console.error("Failed to delete file:", error);
    throw new Error(`File deletion failed: ${error.message}`);
  }
}

/**
 * Utility functions
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

export function getFileType(mimeType) {
  if (!mimeType) return "file";
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType === "application/pdf") return "pdf";
  if (mimeType.startsWith("video/")) return "video";
  if (mimeType.startsWith("audio/")) return "audio";
  return "file";
}

/**
 * Vue 3 Composable for file uploads
 * @returns {Object} Upload utilities and reactive state
 */
export function useFileUpload() {
  const toast = useToast();

  return {
    // Core functions
    createAndUploadFile,
    createAndUploadMultipleFiles,
    validateFile,
    deleteUploadedFile,

    // Utilities
    formatFileSize,
    getFileType,
    UploadState,
    UploadProgress,

    // Helper functions
    showSuccess: (message) =>
      toast.success(message, { timeout: 3000, position: "top-right" }),
    showError: (message) =>
      toast.error(message, { timeout: 5000, position: "top-right" }),
    showInfo: (message) =>
      toast.info(message, { timeout: 3000, position: "top-right" }),

    // File constraints presets
    imageConstraints: {
      maxSize: 5 * 1024 * 1024, // 5MB
      allowedTypes: ["image/jpeg", "image/jpg", "image/png", "image/gif"],
      minWidth: 100,
      minHeight: 100,
      maxWidth: 4000,
      maxHeight: 4000,
    },

    logoConstraints: {
      maxSize: 2 * 1024 * 1024, // 2MB
      allowedTypes: ["image/jpeg", "image/jpg", "image/png"],
      minWidth: 200,
      minHeight: 200,
      maxWidth: 1000,
      maxHeight: 1000,
    },
  };
}

export default {
  createAndUploadFile,
  createAndUploadMultipleFiles,
  validateFile,
  deleteUploadedFile,
  formatFileSize,
  getFileType,
  useFileUpload,
  UploadState,
  UploadProgress,
};
